import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Input from '@mui/joy/Input';
import Sheet from '@mui/joy/Sheet';
import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';
import Typography from '@mui/joy/Typography';
import Alert from '@mui/joy/Alert';
import Stack from '@mui/joy/Stack';

import UserAPI from 'api/user';
import CardLoggedOut from 'components/CardLoggedOut';
import useAuth from 'hooks/useAuth';
import Link from 'components/Link';
import { Success } from 'components/FancyToast';

export function PasswordUpdateStartForm() {
    const [searchParams] = useSearchParams();
	const [email, setEmail] = useState(searchParams.get('email') || '');
	const [status, setStatus] = useState('');
	const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const { currentUser, checkingAuth } = useAuth();

	function startUpdate() {
		if(!email) {
			setError('Forgetting something?');
			setStatus('');
			return;
		}

		UserAPI.passwordStartUpdate(email)
			.then((d) => {
                console.log(d);
				// navigate to login
				setStatus("Check your email for a link to reset your password");
				setError('');
                setDone(true);
			}).catch((err) => {
                console.error(err);
                if(err.message == 'throttled') {
				    setError("Please wait a minute before trying again.");
				    setStatus('');
                }
                else {
				    setError("There was a problem.");
				    setStatus('');
                }
			})
	}

	return (
        <CardLoggedOut size="small">
            <Stack spacing={2}>
                {searchParams.get('legitimize') ?
                    <h2>Let's get you signed up.</h2> :
                    <h2>Let's change your password</h2>}

                {status &&
                    <Alert variant="soft" color="success">{status}</Alert>}

                {error &&
                    <Alert variant="soft" color="danger">{error}</Alert>}

                {!done &&
                    <Stack spacing={2}>
                        <FormControl>
                            <FormLabel>Email</FormLabel>
                            <FormHelperText>We'll email you a link that you can use to (re)set your password</FormHelperText>
                            <Input
                                // html input attribute
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                name="email"
                                type="email"
                                placeholder="your@email.com"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Send it</FormLabel>
                            <Button onClick={startUpdate}>Email Me</Button>
                        </FormControl>

                        {!searchParams.get('legitimize') && !currentUser.key &&
                            <Stack m={1} spacing={2}>
                                <Divider />
                                <Typography
                                    endDecorator={<Link to="/login">Sign In Now</Link>}
                                    fontSize="sm"
                                    sx={{ alignSelf: 'center' }}
                                >
                                    Remember it?
                                </Typography>
                            </Stack>
                        }

                        { currentUser.key && !checkingAuth &&
                            <Stack m={1} spacing={2}>
                                <Divider />
                                <Typography
                                    endDecorator={<Link to="/">Go to Dashboard</Link>}
                                    level="body-xs"
                                    sx={{ alignSelf: 'center' }}
                                >
                                    You are currently signed in.
                                </Typography>
                            </Stack>
                        }

                    </Stack>
                }
            </Stack>
        </CardLoggedOut>
	);
}


export default function PasswordUpdateForm() {
    //	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('');
	const [error, setError] = useState('');
	const { resetTokenKey = "" } = useParams<{ resetTokenKey: string }>();
	const { uuid = "" } = useParams<{ uuid: string }>();
	const navigate = useNavigate();

    // const { token } = useAuth();

	function tryUpdate() {
		// if(!email.length) {
		// 	setError('Email address?');
		// 	return;
		// }
		if(password.length < 7) {
			setError('Password is too short');
			return;
		}
		if (password !== confirmPassword) {
			setError('Passwords do not match');
			return;
		}

		UserAPI.doPasswordChange(resetTokenKey, uuid, password)
			.then(() => {
				Success("Great! Just a second.");
				setError('');
				setTimeout(() => {
					// navigate to login
					navigate('/login');
				}, 1000);
			}).catch((err) => {
				setError("There was an error: " + err.message);
			})

	}


    //    if (token)
    //        return <em>Logged in</em>;

    return (
        <CardLoggedOut size="small">
            <Sheet variant="outlined" color="neutral" sx={{ p: 4, m: 3 }}>
                <Typography level="title-lg">Update your password</Typography>
                {error && <Alert variant="soft" color="danger">{error}</Alert>}
                <FormControl>
                    <FormLabel>Password</FormLabel>
                    <Input
                        onChange={e => setPassword(e.target.value)}
                        value={password}
                        name="password"
                        type="password"
                        placeholder="p4ssw0rd"
                    />
                </FormControl>
                <FormControl>
                    <FormLabel>Password</FormLabel>
                    <Input
                        onChange={e => setConfirmPassword(e.target.value)}
                        value={confirmPassword}
                        name="confirm_password"
                        type="password"
                        placeholder="p4ssw0rd (again)"
                    />
                </FormControl>
                <FormControl>
                    <FormLabel>Save it</FormLabel>
                    <Button onClick={tryUpdate}>Update Password</Button>
                </FormControl>
                <Typography level="body-xs" pt={1}>
                    This will be a different password than your regular Fancy Hands password.
                </Typography>
            </Sheet>
        </CardLoggedOut>
    );
}
